import { BRAND } from '@onuw/roles-data'
import { SITE_ORIGIN } from './config'

/** A public page's one address: on the site's origin, ending with the slash Pages serves it at. */
export const pageUrl = (path: string): string =>
  `${SITE_ORIGIN}${path === '/' ? '' : path.replace(/\/$/, '')}/`

export interface PageMeta {
  /** The page's own title; the site's name is added after it. */
  title: string
  description: string
  /** The route's path ("/rules"). */
  path: string
  /** The whole <title>, for the page whose title already has the site's name (home). */
  fullTitle?: string
  ogTitle?: string
  /** Kept out of search results, its links still followed (privacy, terms). */
  noindex?: boolean
  /** schema.org objects for the page. */
  jsonLd?: Record<string, unknown>[]
}

/**
 * The head of a public page: title, description, the canonical address (the site answers on more than one host and
 * with or without the slash; search engines index this one) and what a link preview shows.
 */
export function pageMeta({ title, description, path, fullTitle, ogTitle, noindex, jsonLd }: PageMeta) {
  const url = pageUrl(path)
  return [
    { title: fullTitle ?? `${title} – ${BRAND.name}` },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: url },
    { property: 'og:title', content: ogTitle ?? fullTitle ?? title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    ...(noindex ? [{ name: 'robots', content: 'noindex, follow' }] : []),
    ...(jsonLd ?? []).map((data) => ({ 'script:ld+json': { '@context': 'https://schema.org', ...data } })),
  ]
}

/** A breadcrumb trail for search results: the pages above this one, then the page itself. */
export const breadcrumbLd = (trail: { name: string; path: string }[]): Record<string, unknown> => ({
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: pageUrl(item.path),
  })),
})
